import React, {useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {CheckBox} from '@rneui/themed';
import {observer} from 'mobx-react';

import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {Paragraph} from '../../components/ui-controls/text/Paragraph';
import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import {AppRoute} from '../../models/AppRoute';
import {OrderItems} from '../../screens/cart/components/OrderItems';
import {useStores} from '../../stores/use-stores';
import {MAX_FORM_WIDTH} from '../constants';
import SignInRequiredModal from '../modals/SignInRequiredModal';
import {GiftCardShopRouteNavigatorScreenProps} from '../types/navigationTypes';

const InvalidCartState = () => (
  <ContentView>
    <View style={{marginBottom: 16}}>
      <ActivityIndicator />
    </View>
    <Text align="center" variant="body">
      Invalid Cart State. Redirecting you to the Gift Card Shop...
    </Text>
  </ContentView>
);

export const GiftCardStoreReviewScreen = observer(
  ({
    navigation: {navigate},
  }: GiftCardShopRouteNavigatorScreenProps<AppRoute.GIFT_CARDS_STORE_REVIEW>) => {
    const {cartStore, userStore} = useStores();
    const isLoggedIn = useIsLoggedIn();

    const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);
    const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);

    const {zenGiftCardRecipientFormValues} = cartStore;

    const handleEdit = () =>
      navigate(AppRoute.GIFT_CARDS_HOME);
    const handleToggleAgreeToTerms = () => setIsAgreeToTerms(!isAgreeToTerms);
    const handleLoginModalBackdropPress = () => setIsLoginModalVisible(false);
    const handleLoginModalPress = () => {
      setIsLoginModalVisible(false);
      userStore.setPostLoginReturnToScreen(AppRoute.GIFT_CARDS_STORE_REVIEW);
      navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
    };
    const handleSubmit = async () => {
      if (isLoggedIn) {
        navigate(AppRoute.GIFT_CARDS_STORE_CHECKOUT);
      } else setIsLoginModalVisible(true);
    };

    const inCartProducts = cartStore.currentOrder?.order_lines?.filter(
      (line: any) => !line.charge,
    );
    const invalidCartState =
      inCartProducts?.length !== 1 || !zenGiftCardRecipientFormValues;

    useEffect(() => {
      if (invalidCartState)
        navigate(AppRoute.GIFT_CARDS_HOME, {isResetForm: true});
    });

    if (invalidCartState) {
      console.debug(
        "Invalid cart state. No 'in cart' products (or more than one) or no recipient form values.",
      );
      return <InvalidCartState />;
    }

    const {
      note,
      recipientName,
      recipientEmail,
      recipientPhoneNumber,
      deliveryDate,
    } = zenGiftCardRecipientFormValues;

    return (
      <ContentView style={styles.main}>
        <ScrollView>
          <OrderItems isGiftCardStore />
          <Text variant="heading2" style={styles.contentStart}>
            TO
          </Text>
          <Paragraph>{recipientName}</Paragraph>
          <Text variant="heading2">YOUR MESSAGE</Text>
          <Paragraph>{note}</Paragraph>
          <Text variant="heading2">EMAIL</Text>
          <Paragraph>{recipientEmail}</Paragraph>
          <Text variant="heading2">MOBILE</Text>
          <Paragraph>{recipientPhoneNumber}</Paragraph>
          <Text variant="heading2">DELIVERY DATE</Text>
          <Paragraph>{deliveryDate.toLocaleDateString()}</Paragraph>
          <Button
            type="outline"
            title="EDIT"
            onPress={handleEdit}
            style={styles.sectionStart}
          />
          <Button
            title="Terms & Conditions"
            type="clear"
            titleStyle={{color: '#0054A6'}}
            containerStyle={{marginTop: 8}}
            onPress={() => navigate(AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS)}
          />
          <CheckBox
            center
            title="AGREE TO T&Cs"
            checked={isAgreeToTerms}
            onPress={handleToggleAgreeToTerms}
            containerStyle={{marginTop: 0, paddingTop: 0}}
          />
          <Button
            title="CONFIRM AND PAY"
            disabled={!isAgreeToTerms}
            onPress={handleSubmit}
          />
        </ScrollView>
        <SignInRequiredModal
          isVisible={isLoginModalVisible}
          onPress={handleLoginModalPress}
          onBackdropPress={handleLoginModalBackdropPress}
        />
      </ContentView>
    );
  },
);

export default GiftCardStoreReviewScreen;

const styles = StyleSheet.create({
  main: {
    maxWidth: MAX_FORM_WIDTH,
  },
  contentStart: {
    marginTop: 16,
  },
  sectionStart: {
    marginTop: 8,
  },
});
